import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';

import { CompanyVendorFile } from '../classes/models/company-vendor-file';
import { GenericObject } from '../interfaces/generic-object';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class FileService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  // The sample COI is stored on the Client's Compliance Settings. Thus, we need to retrieve the correct
  // sample certificate based on the Vendor's Risk Level if the Client has multi-tier compliance.
  getCompanyVendorSampleCOI(
    CompanyId: number,
    VendorId: number,
    query = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${CompanyId}/fileRetriever/sampleCOI`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/${VendorId}/fileRetriever/sampleCOI`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/vendor/${VendorId}/company/${CompanyId}/fileRetriever/sampleCOI`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyVendorFiles(query: GenericObject = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<CompanyVendorFile[]>;
        } = {
          Vendor: () =>
            this.http.get<CompanyVendorFile[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/companyVendorFile/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<CompanyVendorFile[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/companyVendorFile/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<CompanyVendorFile[]>(
              `/api/v2/admin/companyVendor/companyVendorFile/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyVendorFile(query: GenericObject = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<CompanyVendorFile>;
        } = {
          Vendor: () =>
            this.http.get<CompanyVendorFile>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/companyVendorFile/one`,
              { params: query }
            ),
          Client: () =>
            this.http.get<CompanyVendorFile>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/companyVendorFile/one`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<CompanyVendorFile>(
              `/api/v2/admin/companyVendor/companyVendorFile/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyVendorFileS3PresignedUrl(query: GenericObject = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<S3PresignedUrl>;
        } = {
          Admin: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/admin/companyVendor/companyVendorFile/presignedUrl`,
              {
                params: query
              }
            ),
          Client: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/companyVendorFile/presignedUrl`,
              {
                params: query
              }
            ),
          Vendor: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/companyVendorFile/presignedUrl`,
              {
                params: query
              }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyVendorFileCount(query: GenericObject = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${query['CompanyId']}/companyVendor/companyVendorFile/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/${query['ClientRegisteredVendorId'] || query['VendorId'] || query['ExemptVendorId']}/companyVendor/companyVendorFile/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(
              `/api/v2/admin/companyVendor/companyVendorFile/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveCompanyVendorFile(CompanyVendorFile: CompanyVendorFile, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.post(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${CompanyVendorFile.CompanyId}/companyVendor/companyVendorFile/one`,
              CompanyVendorFile,
              { params: query }
            ),
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/${CompanyVendorFile.VendorId}/companyVendor/companyVendorFile/one`,
              CompanyVendorFile,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/companyVendor/companyVendorFile/one`,
              CompanyVendorFile,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveCompanyVendorFiles(
    CompanyVendorFiles: CompanyVendorFile[],
    {
      CompanyId,
      VendorId,
      ExemptVendorId,
      ClientRegisteredVendorId
    }: {
      CompanyId: number;
      VendorId?: number | null;
      ExemptVendorId?: number | null;
      ClientRegisteredVendorId?: number | null;
    },
    query = {}
  ) {
    let vendorString = '';
    if (VendorId) {
      vendorString = `vendor/${VendorId}`;
    }
    if (ExemptVendorId) {
      vendorString = `exemptVendor/${ExemptVendorId}`;
    }
    if (ClientRegisteredVendorId) {
      vendorString = `clientRegisteredVendor/${ClientRegisteredVendorId}`;
    }

    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.post(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${CompanyId}/companyVendor/companyVendorFile/many`,
              CompanyVendorFiles,
              { params: query }
            ),
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/${vendorString}/companyVendor/companyVendorFile/many`,
              CompanyVendorFiles,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/companyVendor/companyVendorFile/many`,
              CompanyVendorFiles,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
